<template lang="html">
  <detail-editor-table
    type="Question"
    :value="value"
    :errors="errors"
    :disabled="disabled"
    sortable
    :json-path="jsonPath"
    :get-path-errors="getPathErrors"
    :clear-path-errors="clearPathErrors"
    :move-path-errors="movePathErrors"
    :item-default="{
      id: null,
      question: null,
      helpText: null,
      warningText: null,
      guestsOnly: false,
      askEachReservee: false,
      dependentQuestionId: null,
      dependentQuestion: null,
      dependentAnswer: null,
      isDependent: false,
      mealPeriodId: mealPeriodId,
      eventId: eventId,
      required: false,
      disabled: false,
      values: [],
      pos: 0
    }"
    :headers="[
      headerDragSort(),
      headerEditor({label: 'Question', field: 'question', editor: {type: 'slot'}}),
      headerEditor({label: 'Answers', field: 'values', editor: {type: 'slot'}}),
      // headerEditor({label: 'Help', field: 'helpText', editor: {required: true, rules: rules().required()}}),
      // headerEditor({label: 'Warning', field: 'warningText', editor: {type: 'slot'}}),
      headerEditor({label: 'Linked Question', field: 'isDependent', editor: {type: 'slot'}, thin: true}),
      headerEditor({label: 'Guests Only', field: 'guestsOnly', editor: {type: 'switch'}, thin: true}),
      ...(discreteCovers ? [headerEditor({label: 'Ask Each Reservee', field: 'askEachReservee', editor: {type: 'switch'}, thin: true})] : []),
      headerEditor({label: 'Required', field: 'required', editor: {type: 'switch'}, thin: true}),
      headerEditor({label: 'Enabled', field: 'disabled', editor: {type: 'switch', invert: true}, thin: true})
    ]"
    @error="addError"
    @add="addedQuestion"
  >
    <!-- Settings editor -->
    <template
      slot="question"
      slot-scope="props"
    >
      <v-autocomplete
        v-if="props.item.isDependent"
        v-model="props.item.dependentQuestion"
        :items="value.filter(q => (q.id !== props.item.id || q.id === null) && q.question && q.question !== props.item.question)"
        label="Linked to Question"
        required
        :rules="rules().required()"
        :disabled="props.disabled"
        item-text="question"
        item-value="question"
        :error-messages="getPathErrors(props.jsonRowPath, 'dependentQuestionId').concat(getPathErrors(props.jsonRowPath, 'dependentQuestion'))"
        @input="props.item.dependentQuestionId = null; clearPathErrors(props.jsonRowPath, 'dependentQuestionId'); clearPathErrors(props.jsonRowPath, 'dependentQuestion')"
      />
      <v-text-field
        v-model="props.item.question"
        required
        :rules="rules().required('Question')"
        placeholder="Enter Question"
        :disabled="props.disabled"
        :error-messages="props.errorMessages"
        @input="props.errorMessages._clear()"
      />
    </template>
    <template
      slot="values"
      slot-scope="props"
    >
      <template v-if="props.item.isDependent">
        <v-text-field
          v-model="props.item.dependentAnswer"
          label="Linked Answer(s)"
          clearable
          :disabled="props.disabled || !value.find(q => q.question === props.item.dependentQuestion)"
          :error-messages="getPathErrors(props.jsonRowPath, 'dependentAnswer')"
          hint="Empty = any; answer1|answer2|...; /RegExp/i"
          @input="clearPathErrors(props.jsonRowPath, 'dependentAnswer')"
        />
      </template>
      <v-card-text>
        <v-chip
          v-for="(value2, i2) in props.item.values"
          :key="i2"
          :outlined="getPathErrors(props.jsonPath, i2, 'value')._hasErrors || !value2.value"
          :color="getPathErrors(props.jsonPath, i2, 'value')._hasErrors || !value2.value ? 'red' : ''"
          @click.stop="toggleQuestionSettingsDialog(props.row)"
        >
          {{ value2.value }}
        </v-chip>
        <v-chip
          v-if="!props.item.values || props.item.values[0] === undefined"
          @click.stop="toggleQuestionSettingsDialog(props.row)"
        >
          Freeform Answer
        </v-chip>
      </v-card-text>
      <v-dialog
        v-model="questionSettingDialogs[props.row]"
        :max-width="350"
        scrollable
      >
        <v-card>
          <v-card-title>
            <div class="headline">
              Answers
            </div>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <div
              v-for="(value2, i2) in props.item.values"
              :key="i2"
            >
              <v-text-field
                v-model="value2.value"
                :rules="rules().required('Value')"
                :label="'Answer Option ' + (i2 + 1)"
                append-icon="mdi-delete"
                :disabled="props.disabled"
                :error-messages="getPathErrors(props.jsonPath, i2, 'value')"
                @input="clearPathErrors(props.jsonPath, i2, 'value')"
                @click:append="() => !props.disabled && deleteQuestionValue(props.item.values, i2, props.errorMessages, props.jsonPath)"
              />
              <v-text-field
                v-if="props.item.isDependent"
                v-model="value2.dependentAnswer"
                label="Linked Answer(s)"
                clearable
                :disabled="props.disabled || !questions.find(q => q.question === props.item.dependentQuestion)"
                :error-messages="getPathErrors(props.jsonPath, i2, 'dependentAnswer')"
                hint="Empty = any; answer1|answer2|...; /RegExp/i"
                @input="clearPathErrors(props.jsonPath, i2, 'dependentAnswer')"
              />
              <v-divider />
            </div>
            <v-card-text v-if="!props.item.values || props.item.values[0] === undefined">
              Any answer currently allowed.  Add an answer to restrict to a selectable list.
            </v-card-text>
            <v-card-title class="pa-0">
              <v-spacer />
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    ripple
                    :disabled="props.disabled"
                    @click.stop="addQuestionValue(props.item.values, props.item.id, props.errorMessages)"
                  >
                    <v-icon class="grey--text text--lighten-1">
                      mdi-plus
                    </v-icon>
                  </v-btn>
                </template>
                <span>Add Answer</span>
              </v-tooltip>
            </v-card-title>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              class="blue--text darken-1"
              text
              @click.native="toggleQuestionSettingsDialog(props.row)"
            >
              Done
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <template
      slot="isDependent"
      slot-scope="props"
    >
      <v-switch
        v-model="props.item.isDependent"
        :color="$ftStyle.switch"
        :error-messages="props.errorMessages"
        :disabled="props.disabled"
        @change="clearDependentQuestionIfNon(props.item); props.errorMessages._clear(); if (!$event) { props.item.dependentQuestionId = null; props.item.dependentAnswer = null }"
      />
    </template>
  </detail-editor-table>
</template>

<script>
import {rules, headerMethods, DetailEditorTable} from '@/ft-vue-core'

export default {
  name: 'QuestionEditor',
  components: {DetailEditorTable},
  mixins: [rules, headerMethods],
  props: {
    jsonPath: String,
    getPathErrors: {
      type: [Function],
      default (path) {
        return []
      }
    },
    clearPathErrors: {
      type: [Function],
      default (path) {
      }
    },
    movePathErrors: {
      type: [Function],
      default (path) {
      }
    },
    value: Array,
    errors: Array,
    mealPeriodId: {
      type: Number,
      default: null
    },
    eventId: {
      type: Number,
      default: null
    },
    disabled: Boolean,
    discreteCovers: Boolean
  },
  data: function () {
    return {
      questionSettingDialogs: []
    }
  },
  computed: {
    questions () {
      return this.value
    }
  },
  methods: {
    clearDependentQuestionIfNon (item) {
      if (item && !item.isDependent) {
        item.dependentQuestion = null
        item.dependentQuestionId = null
      }
    },
    addError (v) {
      this.$emit('error', v)
    },
    toggleQuestionSettingsDialog (row) {
      this.$set(this.questionSettingDialogs, row, !this.questionSettingDialogs[row])
    },
    addQuestionValue (items, parentId, errors) {
      items.push({questionId: parentId, value: null, dependentValue: null, disabled: false})
      this.$emit('add')
    },
    deleteQuestionValue (items, row, errors, jsonPath) {
      if (items[row].value && items[row].value.length) {
        setTimeout(
          () => {
            this.$vPrompt({
              title: 'Remove ',
              titleIcon: 'mdi-delete',
              titleIconColor: 'error',
              text: 'Are you sure you want to remove ' + items[row].value + '?',
              confirmText: 'Remove',
              confirmAction: () => {
                this.clearPathErrors(jsonPath, row, 'value') // clear any errors before we remove it
                items.splice(row, 1)
              },
              disableConfirm: this.disabled,
              cancelDialog: this.disabled,
              cancelAction: true
            })
          }, 0
        )
      } else {
        items.splice(row, 1)
      }
    },
    addedQuestion () {
      this.$emit('add')
    }
  }
}
</script>
